
import React from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { User, Sparkles, TrendingUp, Database, BarChart3, Brain } from 'lucide-react';
import { cn } from '@/lib/utils';
import { ChippieMessage } from '@/types/database.types';

interface ChatMessageProps {
  message: ChippieMessage;
  isLoading?: boolean;
}

const ChatMessage: React.FC<ChatMessageProps> = ({ message, isLoading = false }) => {
  const isUser = message.role === 'user';
  
  const getIntentIcon = (intent: string) => {
    switch (intent) {
      case 'DATA_QUERY': return <Database className="h-3 w-3" />;
      case 'ANALYTICS': return <BarChart3 className="h-3 w-3" />;
      case 'INSIGHTS': return <Brain className="h-3 w-3" />;
      case 'TRENDS': return <TrendingUp className="h-3 w-3" />;
      default: return <Sparkles className="h-3 w-3" />;
    }
  };

  const getComplexityColor = (complexity: string) => {
    switch (complexity) {
      case 'simple': return 'bg-green-100 text-green-800';
      case 'moderate': return 'bg-yellow-100 text-yellow-800';
      case 'complex': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  return (
    <div className={cn(
      "flex gap-3 max-w-4xl",
      isUser ? "ml-auto flex-row-reverse" : "mr-auto"
    )}>
      {/* Avatar */}
      <div className={cn(
        "flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center",
        isUser ? "bg-blue-500" : "bg-gradient-to-r from-blue-500 to-purple-600"
      )}>
        {isUser ? (
          <User className="h-4 w-4 text-white" />
        ) : (
          <Sparkles className="h-4 w-4 text-white" />
        )}
      </div>

      {/* Message Content */}
      <div className={cn(
        "flex-1 space-y-3",
        isUser ? "items-end" : "items-start"
      )}>
        <Card className={cn(
          "max-w-3xl",
          isUser ? "bg-blue-50 border-blue-200 ml-auto" : "bg-white"
        )}>
          <CardContent className="p-4">
            {/* Message Text */}
            <div className={cn(
              "prose prose-sm max-w-none",
              isUser ? "text-blue-900" : "text-gray-900"
            )}>
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
                    <div className="w-2 h-2 bg-blue-500 rounded-full animate-bounce"></div>
                  </div>
                  <span className="text-sm text-gray-500">Analyzing your request...</span>
                </div>
              ) : (
                <div className="whitespace-pre-wrap">{message.content}</div>
              )}
            </div>

            {/* Enhanced Metadata for Assistant Messages */}
            {!isUser && message.metadata && !isLoading && (
              <div className="mt-4 space-y-3">
                <Separator />
                
                {/* Query Information */}
                <div className="flex flex-wrap gap-2 items-center">
                  {message.metadata.intent && (
                    <Badge variant="outline" className="text-xs">
                      {getIntentIcon(message.metadata.intent)}
                      <span className="ml-1">{message.metadata.intent.replace('_', ' ')}</span>
                    </Badge>
                  )}
                  
                  {message.metadata.metadata?.query_complexity && (
                    <Badge className={cn("text-xs", getComplexityColor(message.metadata.metadata.query_complexity))}>
                      {message.metadata.metadata.query_complexity} query
                    </Badge>
                  )}
                  
                  {message.metadata.metadata?.records_analyzed && message.metadata.metadata.records_analyzed > 0 && (
                    <Badge variant="secondary" className="text-xs">
                      <Database className="h-3 w-3 mr-1" />
                      {message.metadata.metadata.records_analyzed.toLocaleString()} records
                    </Badge>
                  )}
                </div>

                {/* Data Insights */}
                {message.metadata.insights && message.metadata.insights.insights && message.metadata.insights.insights.length > 0 && (
                  <div className="bg-blue-50 rounded-lg p-3 space-y-2">
                    <h4 className="text-sm font-medium text-blue-900 flex items-center gap-2">
                      <Brain className="h-4 w-4" />
                      Key Insights
                    </h4>
                    <ul className="text-sm text-blue-800 space-y-1">
                      {message.metadata.insights.insights.map((insight, index) => (
                        <li key={index} className="flex items-start gap-2">
                          <span className="text-blue-400 mt-1">•</span>
                          {insight}
                        </li>
                      ))}
                    </ul>
                  </div>
                )}

                {/* Enhanced Features */}
                {message.metadata.metadata?.enhanced_features && (
                  <div className="flex flex-wrap gap-1">
                    {message.metadata.metadata.enhanced_features.map((feature, index) => (
                      <Badge key={index} variant="outline" className="text-xs bg-green-50 text-green-700 border-green-200">
                        ✓ {feature.replace('_', ' ')}
                      </Badge>
                    ))}
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Timestamp */}
        <div className={cn(
          "text-xs text-gray-500 px-1",
          isUser ? "text-right" : "text-left"
        )}>
          {new Date(message.created_at).toLocaleTimeString([], { 
            hour: '2-digit', 
            minute: '2-digit' 
          })}
        </div>
      </div>
    </div>
  );
};

export default ChatMessage;
